import { useState, useCallback } from 'react';

interface ChairModel {
  id: string;
  name: string;
  subtitle: string;
  images: string[];
  highlights: string[];
  price: string;
  description: string;
}

const chairData: ChairModel[] = [
  {
    id: 'conferenza',
    name: 'Cadeira Conferenza',
    subtitle: 'Empilhável com engate enfileirador',
    description: 'Cadeira empilhável com estrutura em tubo oblongo reforçado, solda MIG e pintura epóxi. Assento e encosto com espuma injetada para máximo conforto mesmo em eventos longos.',
    price: 'A partir de R$ 289,00',
    images: [
      '/media/ia/0373.webp',
    ],
    highlights: ['Tubo oblongo reforçado', 'Solda MIG de alta resistência', 'Pintura epóxi anticorrosiva', 'Espuma injetada no assento e encosto', 'Engate enfileirador incluso'],
  },
  {
    id: 'diretor',
    name: 'Poltrona Diretor',
    subtitle: 'Conforto premium para liderança',
    description: 'Poltrona diretor com estrutura em tubo oblongo reforçado, solda MIG e pintura epóxi. Assento e encosto com espuma injetada que garante conforto prolongado para pastores, bispos e líderes.',
    price: 'A partir de R$ 389,00',
    images: [
      '/media/ia/0374.webp',
    ],
    highlights: ['Tubo oblongo reforçado', 'Solda MIG de alta resistência', 'Pintura epóxi anticorrosiva', 'Espuma injetada no assento e encosto', 'Design executivo ergonômico'],
  },
  {
    id: 'meet',
    name: 'Poltrona Meet',
    subtitle: 'Robustez máxima com garantia de 7 anos',
    description: 'A cadeira mais resistente da linha. Suporta até 200kg por assento, estrutura em tubo de aço com engate de aço reforçado, assento e encosto em compensado naval com espuma injetada D55 de alta densidade. Feita para igrejas que precisam de durabilidade extrema sem abrir mão do conforto.',
    price: 'A partir de R$ 449,00',
    images: [
      '/media/produtos/0375.png',
    ],
    highlights: ['Garantia de 7 anos', 'Suporta até 200kg por assento', 'Engate de aço reforçado', 'Espuma injetada D55 de alta densidade', 'Assento em compensado naval'],
  },
];

export default function ChairGallery() {
  const [activeTab, setActiveTab] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  const currentModel = chairData[activeTab];

  const goToSlide = useCallback((index: number) => {
    if (isAnimating || index === activeSlide) return;
    setDirection(index > activeSlide ? 'right' : 'left');
    setIsAnimating(true);
    setActiveSlide(index);
    setTimeout(() => setIsAnimating(false), 400);
  }, [activeSlide, isAnimating]);

  const nextSlide = useCallback(() => {
    const next = (activeSlide + 1) % currentModel.images.length;
    goToSlide(next);
  }, [activeSlide, currentModel.images.length, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = (activeSlide - 1 + currentModel.images.length) % currentModel.images.length;
    goToSlide(prev);
  }, [activeSlide, currentModel.images.length, goToSlide]);

  const switchTab = useCallback((index: number) => {
    if (index === activeTab) return;
    setActiveTab(index);
    setActiveSlide(0);
    setIsAnimating(false);
  }, [activeTab]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6">
      <div className="text-center mb-10">
        <span className="text-[#E8610A] text-xs font-bold uppercase tracking-widest">Galeria de produtos</span>
        <h2 className="text-gray-900 text-2xl md:text-3xl font-bold mt-2">
          Conheça nossas <span className="text-[#E8610A]">cadeiras em detalhes</span>
        </h2>
        <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
          Clique nas abas para alternar entre os modelos. Veja as especificações técnicas e solicite seu orçamento.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-gray-100 rounded-full p-1 gap-1">
          {chairData.map((chair, idx) => (
            <button
              key={chair.id}
              onClick={() => switchTab(idx)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeTab === idx
                  ? 'bg-[#E8610A] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              {chair.name}
            </button>
          ))}
        </div>
      </div>

      {/* Gallery + Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Left: Image Area */}
        <div className="relative rounded-2xl overflow-hidden bg-gray-50 border border-gray-100">
          <div className="relative w-full h-80 md:h-[480px] overflow-hidden">
            {currentModel.images.length > 0 ? (
              currentModel.images.map((img, idx) => (
                <div
                  key={`${activeTab}-${idx}`}
                  className={`absolute inset-0 transition-all duration-400 ease-out ${
                    idx === activeSlide
                      ? 'opacity-100 translate-x-0 z-10'
                      : direction === 'right'
                        ? 'opacity-0 translate-x-8 z-0'
                        : 'opacity-0 -translate-x-8 z-0'
                  }`}
                >
                  <img
                    src={img}
                    alt={`${currentModel.name} - foto ${idx + 1}`}
                    className="w-full h-full object-contain object-center p-4"
                  />
                </div>
              ))
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-200 rounded-full mb-3">
                  <i className="ri-image-line text-gray-400 text-2xl"></i>
                </div>
                <p className="text-gray-400 text-sm">Foto do produto em breve</p>
              </div>
            )}
          </div>

          {currentModel.images.length > 1 && (
            <>
              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-md z-20 transition-all duration-200 cursor-pointer"
                aria-label="Foto anterior"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-s-line text-gray-700 text-xl"></i>
                </div>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-md z-20 transition-all duration-200 cursor-pointer"
                aria-label="Próxima foto"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-gray-700 text-xl"></i>
                </div>
              </button>

              {/* Slide Counter */}
              <div className="absolute top-4 right-4 bg-black/50 text-white text-xs font-medium px-3 py-1 rounded-full z-20">
                {activeSlide + 1} / {currentModel.images.length}
              </div>

              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
                {currentModel.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    className={`rounded-full transition-all duration-200 cursor-pointer ${
                      idx === activeSlide
                        ? 'w-6 h-2 bg-[#E8610A]'
                        : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Ir para foto ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: Info */}
        <div className="flex flex-col gap-5">
          <div>
            <span className={`inline-block text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${
              activeTab === 0 ? 'bg-[#E8610A]' : activeTab === 1 ? 'bg-gray-700' : 'bg-red-600'
            }`}>
              {activeTab === 0 ? 'Conferenza' : activeTab === 1 ? 'Diretor' : 'Meet'}
            </span>
          </div>
          <div>
            <h3 className="text-gray-900 text-2xl font-bold">{currentModel.name}</h3>
            <p className="text-gray-400 text-sm mt-1">{currentModel.subtitle}</p>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">{currentModel.description}</p>

          <div className="flex flex-col gap-2">
            {currentModel.highlights.map((h) => (
              <div key={h} className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-[#E8610A] text-sm"></i>
                </div>
                <span className="text-gray-700 text-sm">{h}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#fafafa] rounded-xl p-5 border border-gray-100">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">Preço</p>
            <p className="text-[#E8610A] text-xl font-bold">{currentModel.price}</p>
            <p className="text-gray-400 text-xs mt-1">Varia conforme quantidade e cor</p>
          </div>

          <a
            href={`https://wa.me/5581982030285?text=Ol%C3%A1%2C%20tenho%20interesse%20na%20${encodeURIComponent(currentModel.name)}%20e%20quero%20solicitar%20um%20or%C3%A7amento!`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-6 py-3.5 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide w-fit"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            SOLICITAR ORÇAMENTO
          </a>
        </div>
      </div>

      {/* Thumbnails - only show if more than 1 image */}
      {currentModel.images.length > 1 && (
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          {currentModel.images.map((img, idx) => (
            <button
              key={`thumb-${activeTab}-${idx}`}
              onClick={() => goToSlide(idx)}
              className={`w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                idx === activeSlide
                  ? 'border-[#E8610A] shadow-md'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                src={img}
                alt={`${currentModel.name} miniatura ${idx + 1}`}
                className="w-full h-full object-contain object-center p-1"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}